// ================================================================
// src/pages/FreeStudyCourse.jsx
// صفحه دوره‌های مطالعه آزاد — نسخه‌ی نهایی پرداخت‌محور (v2)
// ----------------------------------------------------------------
// مسیر: /free-study/:courseId
// ================================================================

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFreeStudyCourse } from '../data/freeStudyData';
import {
  // Hooks
  useCoursePurchase,
  useCourseProgress,
  usePricing,
  useVideoStatus,
  // UI
  PricingCard,
  VideoLockBadge,
  PurchaseModal,
  ReceiptView,
  VideoOverlay,
  ConfettiCelebration,
  // Utils
  computeVideoPrice,
  isEffectivelyFree,
  formatPrice,
  toPersianDigits,
  getSmartSuggestion,
} from '../freeStudy';
import './FreeStudyCourse.css';

// ================================================================
// Helpers
// ================================================================
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '۰ دقیقه';
  if (minutes < 60) return `${toPersianDigits(minutes)} دقیقه`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0
    ? `${toPersianDigits(h)} ساعت`
    : `${toPersianDigits(h)}:${String(m).padStart(2, '0')} ساعت`;
};

const timeAgo = (ts) => {
  if (!ts) return 'هنوز بازدید نشده';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'همین الان';
  if (mins < 60) return `${toPersianDigits(mins)} دقیقه پیش`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${toPersianDigits(hours)} ساعت پیش`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${toPersianDigits(days)} روز پیش`;
  return `${toPersianDigits(Math.floor(days / 30))} ماه پیش`;
};

// ================================================================
// 🎬 کامپوننت اصلی
// ================================================================
export default function FreeStudyCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ کاربر مهمان = null (نه 'unknown') — جداسازی کامل داده‌ها
  const userId = user?.id || null;

  const course = useMemo(() => getFreeStudyCourse(courseId), [courseId]);

  // ===== هوک‌های سیستم خرید =====
  const {
    state: purchaseState,
    unlockedSet,
    isProcessing,
    buyVideo,
    buyBundle,
  } = useCoursePurchase(userId, course);

  const {
    state: progress,
    completedSet,
    markVisited,
    markVideoWatched,
    unmarkVideo,
    saveNote,
  } = useCourseProgress(userId, course);

  const pricing = usePricing(course, purchaseState);

  const getVideoStatus = useVideoStatus(
    unlockedSet,
    completedSet,
    progress.lastVideoId
  );

  // ===== state های UI =====
  // ===== state های UI =====
  const [playingIndex, setPlayingIndex] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showReceipts, setShowReceipts] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    mode: 'bundle',
    videoId: null,
    result: null,
  });
  const [studentCount, setStudentCount] = useState(0);  // 🆕 شمارنده‌ی واقعی دانش‌آموزان

  const prevAllDoneRef = useRef(false);
  const heroRef = useRef(null);

  // 🆕 محاسبه‌ی تعداد دانش‌آموزان واقعی این دوره از سرور
  useEffect(() => {
    if (!courseId) return;
    let cancelled = false;

    const load = async () => {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const userIds = new Set();

      // ─── ۱. از freeStudyProgress (هر کسی که فعالیت داشت) ───
      try {
        const res = await fetch(`${API_BASE}/freeStudyProgress`);
        if (res.ok) {
          const list = await res.json();
          if (Array.isArray(list)) {
            for (const rec of list) {
              if (!rec?.userId || !rec?.courseId) continue;
              if (String(rec.courseId) !== String(courseId)) continue;
              const hasActivity =
                (Array.isArray(rec.completedVideoIds) && rec.completedVideoIds.length > 0) ||
                rec.lastVideoId ||
                rec.lastVisited;
              if (hasActivity) userIds.add(String(rec.userId));
            }
          }
        }
      } catch (e) {
        console.warn('[FreeStudyCourse] progress fetch failed:', e);
      }

      // ─── ۲. از freeStudyPurchases (هر کسی که خرید داشت) ───
      try {
        const res = await fetch(`${API_BASE}/freeStudyPurchases`);
        if (res.ok) {
          const list = await res.json();
          if (Array.isArray(list)) {
            for (const rec of list) {
              if (!rec?.userId || !rec?.courseId) continue;
              if (String(rec.courseId) !== String(courseId)) continue;
              const hasTx = Array.isArray(rec.transactions) && rec.transactions.length > 0;
              if (hasTx) userIds.add(String(rec.userId));
            }
          }
        }
      } catch (e) {
        console.warn('[FreeStudyCourse] purchases fetch failed:', e);
      }

      if (!cancelled) setStudentCount(userIds.size);
    };

    load();
    const onFocus = () => load();
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [courseId]);

  // ===== تم شب/روز (اتصال به navbar) =====
  const [isDark, setIsDark] = useState(() => {
    try {
      return localStorage.getItem('navbar2-drawer-dark') === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') {
        setIsDark(e.newValue === '1');
      }
    };
    window.addEventListener('storage', handleStorage);

    const interval = setInterval(() => {
      try {
        const current = localStorage.getItem('navbar2-drawer-dark') === '1';
        setIsDark((prev) => (prev !== current ? current : prev));
      } catch {}
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  // ===== ریست مودال و پلیر هنگام تغییر کاربر =====
  useEffect(() => {
    setModal({ open: false, mode: 'bundle', videoId: null, result: null });
    setPlayingIndex(null);
    setShowReceipts(false);
  }, [userId]);

  // ===== ثبت بازدید در ورود =====
  useEffect(() => {
    if (userId && course) markVisited();
  }, [markVisited, userId, course]);

  // ===== پارالاکس هدر =====
  useEffect(() => {
    if (!heroRef.current) return;
    const el = heroRef.current;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          el.style.setProperty('--scroll-y', `${window.scrollY}px`);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ===== آمار =====
  const totalVideos = course?.videos?.length || 0;
  const watchedCount = progress.completedVideoIds?.length || 0;
  const allDone = totalVideos > 0 && watchedCount === totalVideos;

  // ===== جشن تکمیل =====
  useEffect(() => {
    if (allDone && !prevAllDoneRef.current) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 4500);
      prevAllDoneRef.current = true;
      return () => clearTimeout(t);
    }
    if (!allDone) prevAllDoneRef.current = false;
  }, [allDone]);

  // ===== باز کردن یک ویدیو =====
  const openVideo = useCallback(
    (index) => {
      const video = course?.videos?.[index];
      if (!video) return;
      setPlayingIndex(index);
    },
    [course]
  );

  // ===== درخواست خرید یک ویدیو =====
  const requestPurchase = useCallback(
    (videoId) => {
      const video = course?.videos?.find((v) => v.id === videoId);
      if (!video) return;

      // اگر قبلاً باز است، مستقیم باز کن
      if (unlockedSet.has(videoId) || isEffectivelyFree(video)) {
        const idx = course.videos.findIndex((v) => v.id === videoId);
        if (idx >= 0) setPlayingIndex(idx);
        return;
      }

      if (!userId) {
        // eslint-disable-next-line no-alert
        alert('برای خرید ابتدا وارد حساب کاربری خود شوید');
        return;
      }

      setModal({
        open: true,
        mode: 'video',
        videoId,
        result: null,
      });
    },
    [userId, course, unlockedSet]
  );

  // ===== درخواست خرید بسته‌ی کل دوره =====
  const requestBundle = useCallback(() => {
    if (!userId) {
      // eslint-disable-next-line no-alert
      alert('برای خرید ابتدا وارد حساب کاربری خود شوید');
      return;
    }
    setModal({
      open: true,
      mode: 'bundle',
      videoId: null,
      result: null,
    });
  }, [userId]);

  // ===== تأیید و انجام خرید =====
  const confirmPurchase = useCallback(async () => {
    if (!modal.open) return;
    if (!course) return;

    let res;
    try {
      if (modal.mode === 'bundle') {
        res = await buyBundle();
      } else if (modal.mode === 'video' && modal.videoId) {
        res = await buyVideo(modal.videoId);
      } else {
        res = { ok: false, message: 'حالت خرید نامعتبر' };
      }
    } catch (e) {
      res = { ok: false, message: 'خطای غیرمنتظره در پرداخت' };
    }

    setModal((m) => ({ ...m, result: res }));
  }, [modal, buyBundle, buyVideo, course]);

  // ===== بستن مودال =====
  const closeModal = useCallback(() => {
    setModal((m) => ({ ...m, open: false, result: null }));
  }, []);

  // ===== دوره پیدا نشد =====
  if (!course) {
    return (
      <div className="fsc-page" data-theme={isDark ? 'dark' : 'light'}>
        <div className="fsc-error">
          <span className="fsc-error-icon">🔍</span>
          <h2>دوره یافت نشد</h2>
          <p>متأسفیم، دوره‌ای با این شناسه وجود ندارد.</p>
          <button
            className="fsc-btn fsc-btn-primary"
            onClick={() => navigate('/books')}
          >
            <i className="fas fa-arrow-right" />
            بازگشت به کتاب‌ها
          </button>
        </div>
      </div>
    );
  }

  // ===== محاسبات پیشرفت =====
  const percent =
    totalVideos > 0 ? Math.round((watchedCount / totalVideos) * 100) : 0;

  const totalMinutes = course.videos.reduce(
    (s, v) => s + (Number(v.duration) || 0),
    0
  );

  const RING_R = 52;
  const RING_C = 2 * Math.PI * RING_R;
  const ringOffset = RING_C * (1 - percent / 100);

  // ===== اطلاعات مودال =====
  const modalVideo = modal.videoId
    ? course.videos.find((v) => v.id === modal.videoId)
    : null;

  // ✅ پیشنهاد هوشمند برای مودال (فقط وقتی حالت video است)
  const smartSuggestion =
    modal.open && modal.mode === 'video' && modal.videoId
      ? getSmartSuggestion(course, purchaseState, modal.videoId)
      : null;

  // ===== رندر =====
  return (
    <div
      className="fsc-page"
      data-theme={isDark ? 'dark' : 'light'}
      style={{ '--course-color': course.color || '#4f46e5' }}
    >
      {/* ============================================ */}
      {/* LAYER 1: HERO با پارالاکس */}
      {/* ============================================ */}
      <div className="fsc-hero" ref={heroRef}>
        <div
          className="fsc-hero-bg"
          style={{ backgroundImage: `url(${course.image})` }}
        >
          <div className="fsc-hero-overlay" />
          <div className="fsc-hero-aurora" />

          {/* ذرات شناور */}
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={i}
              className={`fsc-particle p${i + 1}`}
              style={{ '--delay': `${i * 0.3}s` }}
            />
          ))}

          {/* دکمه بازگشت */}
          <button
            className="fsc-back-btn"
            onClick={() => navigate('/books')}
            aria-label="بازگشت"
          >
            <i className="fas fa-arrow-right" />
            <span>بازگشت</span>
          </button>

          {/* برچسب دسته و سطح */}
          <div className="fsc-hero-badges">
            <span className="fsc-badge fsc-badge-cat">
              <i className="fas fa-tag" /> {course.category}
            </span>
            <span className="fsc-badge fsc-badge-level">
              <i className="fas fa-signal" /> {course.level}
            </span>
          </div>

          {/* عنوان بزرگ */}
          <div className="fsc-hero-content">
            <h1 className="fsc-hero-title">{course.title}</h1>
            {course.subtitle && (
              <p className="fsc-hero-subtitle">{course.subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* LAYER 2: Glass Info Card */}
      {/* ============================================ */}
      <div className="fsc-info-wrap">
        <div className="fsc-info-card">
          <div className="fsc-info-glow" />

          <div className="fsc-info-top">
            <div className="fsc-instructor">
              {course.instructorImage ? (
                <img
                  src={course.instructorImage}
                  alt={course.instructor}
                  className="fsc-instructor-avatar"
                />
              ) : (
                <div className="fsc-instructor-avatar fsc-avatar-fallback">
                  {course.instructor?.slice(0, 1) || 'م'}
                </div>
              )}
              <div className="fsc-instructor-meta">
                <span className="fsc-instructor-label">مدرس دوره</span>
                <span className="fsc-instructor-name">
                  {course.instructor}
                </span>
              </div>
            </div>

            <div className="fsc-stats-row">
              <div className="fsc-stat">
                <i className="fas fa-video" />
                <span className="fsc-stat-val">
                  {toPersianDigits(totalVideos)}
                </span>
                <span className="fsc-stat-lbl">ویدیو</span>
              </div>
              <div className="fsc-stat">
                <i className="fas fa-clock" />
                <span className="fsc-stat-val">
                  {formatDuration(totalMinutes)}
                </span>
                <span className="fsc-stat-lbl">مدت کل</span>
              </div>
              <div className="fsc-stat">
                <i className="fas fa-users" />
                <span className="fsc-stat-val">
                  {toPersianDigits(studentCount)}
                </span>
                <span className="fsc-stat-lbl">دانش‌آموز</span>
              </div>
            </div>
          </div>

          <div className="fsc-divider" />

          <p className="fsc-description">{course.description}</p>

          {course.tags?.length > 0 && (
            <div className="fsc-tags">
              {course.tags.map((t, i) => (
                <span key={i} className="fsc-tag">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* LAYER 3: Pricing Card */}
      {/* ============================================ */}
      <div className="fsc-progress-section">
        <PricingCard
          course={course}
          pricing={pricing}
          onBuyBundle={requestBundle}
          isProcessing={isProcessing}
        />
      </div>

      {/* ============================================ */}
      {/* LAYER 4: Progress Dashboard */}
      {/* ============================================ */}
      <div className="fsc-progress-section">
        <div className="fsc-progress-card">
          <div className="fsc-ring-wrap">
            <svg className="fsc-ring-svg" viewBox="0 0 120 120">
              <defs>
                <linearGradient
                  id="fscRingGrad"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stopColor={course.color || '#4f46e5'}
                  />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
              </defs>
              <circle className="fsc-ring-track" cx="60" cy="60" r={RING_R} />
              <circle
                className="fsc-ring-progress"
                cx="60"
                cy="60"
                r={RING_R}
                strokeDasharray={RING_C}
                strokeDashoffset={ringOffset}
              />
            </svg>
            <div className="fsc-ring-inner">
              <span className="fsc-ring-percent">
                {toPersianDigits(percent)}٪
              </span>
              <span className="fsc-ring-label">پیشرفت</span>
            </div>
          </div>

          <div className="fsc-progress-info">
            <div className="fsc-progress-row">
              <i
                className="fas fa-check-circle"
                style={{ color: '#22c55e' }}
              />
              <span>
                <strong>{toPersianDigits(watchedCount)}</strong> از{' '}
                <strong>{toPersianDigits(totalVideos)}</strong> ویدیو دیده‌شده
              </span>
            </div>
            <div className="fsc-progress-row">
              <i className="fas fa-history" style={{ color: '#8b5cf6' }} />
              <span>آخرین بازدید: {timeAgo(progress.lastVisited)}</span>
            </div>
            {allDone ? (
              <div className="fsc-progress-row fsc-done-row">
                <i className="fas fa-trophy" style={{ color: '#fbbf24' }} />
                <span>🎉 تبریک! این دوره را کامل کردی</span>
              </div>
            ) : (
              <div className="fsc-progress-row">
                <i
                  className="fas fa-bullseye"
                  style={{ color: '#ec4899' }}
                />
                <span>
                  <strong>
                    {toPersianDigits(totalVideos - watchedCount)}
                  </strong>{' '}
                  ویدیو تا اتمام
                </span>
              </div>
            )}
            {purchaseState.transactions?.length > 0 && (
              <button
                type="button"
                className="fsc-receipt-btn"
                onClick={() => setShowReceipts(true)}
              >
                <i className="fas fa-receipt" /> مشاهده‌ی خریدها
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* LAYER 5: Timeline */}
      {/* ============================================ */}
      <div className="fsc-timeline-section">
        <div className="fsc-section-title">
          <i className="fas fa-list-ol" />
          <span>مسیر یادگیری</span>
          <span className="fsc-section-badge">
            {toPersianDigits(totalVideos)} قسمت
          </span>
        </div>

        <div className="fsc-timeline">
          {/* خط اتصال با پیشرفت */}
          <div className="fsc-timeline-line">
            <div
              className="fsc-timeline-line-fill"
              style={{
                height:
                  totalVideos > 1
                    ? `${(watchedCount / totalVideos) * 100}%`
                    : '0%',
              }}
            />
          </div>

          {course.videos.map((video, index) => {
            const status = getVideoStatus(video);
            const isWatched = status === 'watched';
            const isCurrent = status === 'current';
            const isLocked = status === 'locked';
            const isFree = isEffectivelyFree(video);
            const suggestion = isLocked
              ? getSmartSuggestion(course, purchaseState, video.id)
              : null;

            return (
              <div
                key={video.id}
                className={`fsc-timeline-item fsc-${status}`}
                onClick={() => {
                  if (isLocked) requestPurchase(video.id);
                  else openVideo(index);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (isLocked) requestPurchase(video.id);
                    else openVideo(index);
                  }
                }}
              >
                {/* Node */}
                <div className="fsc-timeline-node">
                  {isWatched ? (
                    <i className="fas fa-check" />
                  ) : isCurrent ? (
                    <i className="fas fa-play" />
                  ) : isLocked ? (
                    <i className="fas fa-lock" />
                  ) : (
                    <span>{toPersianDigits(index + 1)}</span>
                  )}
                </div>

                {/* Card */}
                <div className="fsc-video-card">
                  <div className="fsc-video-thumb fsc-video-thumb-locked-wrap">
                    <div
                      className={`fsc-video-thumb-inner ${
                        isLocked ? 'fsc-video-thumb-locked' : ''
                      }`}
                      style={{
                        backgroundImage: `url(${
                          video.thumbnail || course.image
                        })`,
                      }}
                    />

                    {isLocked && (
                      <div className="fsc-video-lock-icon">
                        <i className="fas fa-lock" />
                      </div>
                    )}

                    {!isLocked && (
                      <div className="fsc-video-play-icon">
                        <i
                          className={`fas fa-${isWatched ? 'redo' : 'play'}`}
                        />
                      </div>
                    )}

                    {video.duration && (
                      <span className="fsc-video-duration">
                        <i className="far fa-clock" />{' '}
                        {formatDuration(video.duration)}
                      </span>
                    )}
                  </div>

                  <div className="fsc-video-info">
                    <div className="fsc-video-title-row">
                      <h3 className="fsc-video-title">{video.title}</h3>

                      {isWatched && (
                        <span className="fsc-video-watched-badge">
                          <i className="fas fa-check-circle" /> دیده‌شده
                        </span>
                      )}

                      {isLocked && !isFree && (
                        <VideoLockBadge
                          price={computeVideoPrice(video)}
                          currency={course.currency}
                        />
                      )}

                      {isFree && !isWatched && (
                        <span className="fsc-video-free-badge">
                          <i className="fas fa-gift" /> رایگان
                        </span>
                      )}
                    </div>

                    {video.description && (
                      <p className="fsc-video-desc">{video.description}</p>
                    )}

                    <div className="fsc-video-actions">
                      {isLocked ? (
                        <>
                          <button
                            type="button"
                            className="fsc-video-lock-cta"
                            onClick={(e) => {
                              e.stopPropagation();
                              requestPurchase(video.id);
                            }}
                          >
                            <i className="fas fa-unlock" /> باز کردن
                          </button>

                          {suggestion?.type === 'bundle' &&
                            suggestion.saving > 0 && (
                              <span className="fsc-video-suggestion">
                                💡 با بسته‌ی کل دوره {formatPrice(
                                  suggestion.saving,
                                  course.currency
                                )}{' '}
                                ارزان‌تر می‌شود
                              </span>
                            )}
                        </>
                      ) : (
                        <span className="fsc-video-action-hint">
                          <i className="fas fa-play-circle" /> برای پخش
                          کلیک کن
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============================================ */}
      {/* LAYER 6: Video Player Overlay */}
      {/* ============================================ */}
      {playingIndex !== null && course.videos[playingIndex] && (
        <VideoOverlay
          course={course}
          videoIndex={playingIndex}
          video={course.videos[playingIndex]}
          isWatched={completedSet.has(course.videos[playingIndex].id)}
          isLocked={getVideoStatus(course.videos[playingIndex]) === 'locked'}
          notes={progress.notes?.[course.videos[playingIndex].id] || ''}
          onClose={() => setPlayingIndex(null)}
          onWatched={() =>
            markVideoWatched(course.videos[playingIndex].id)
          }
          onUnwatch={() => unmarkVideo(course.videos[playingIndex].id)}
          onSaveNote={(text) =>
            saveNote(course.videos[playingIndex].id, text)
          }
          onNext={() => {
            const currentVideo = course.videos[playingIndex];
            const nextIndex = playingIndex + 1;
            if (nextIndex >= course.videos.length) return;

            markVideoWatched(currentVideo.id);
            const nextVideo = course.videos[nextIndex];

            // اگر ویدیوی بعدی قفل است، پلیر را ببند و مودال خرید باز کن
            if (getVideoStatus(nextVideo) === 'locked') {
              setPlayingIndex(null);
              requestPurchase(nextVideo.id);
            } else {
              setPlayingIndex(nextIndex);
            }
          }}
          onPrev={() => {
            if (playingIndex > 0) setPlayingIndex(playingIndex - 1);
          }}
          hasNext={playingIndex < course.videos.length - 1}
          hasPrev={playingIndex > 0}
          onRequestPurchase={() =>
            requestPurchase(course.videos[playingIndex].id)
          }
        />
      )}

      {/* ============================================ */}
      {/* LAYER 7: Purchase Modal */}
      {/* ============================================ */}
      <PurchaseModal
        open={modal.open}
        onClose={closeModal}
        mode={modal.mode}
        course={course}
        video={modalVideo}
        pricing={pricing}
        onConfirm={confirmPurchase}
        isProcessing={isProcessing}
        result={modal.result}
      />

      {/* ============================================ */}
      {/* LAYER 8: Receipts */}
      {/* ============================================ */}
      <ReceiptView
        open={showReceipts}
        onClose={() => setShowReceipts(false)}
        transactions={purchaseState.transactions}
        currency={course.currency}
      />

      {/* ============================================ */}
      {/* LAYER 9: Confetti */}
      {/* ============================================ */}
      {showConfetti && <ConfettiCelebration course={course} />}
    </div>
  );
}